import { useState, useRef, useEffect } from 'react';
import {
  addDays,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  format,
  differenceInCalendarDays,
  isSameDay,
  isWeekend,
  isMonday,
  parseISO,
  isValid,
  isBefore,
  isAfter,
  startOfDay,
  startOfISOWeek,
} from 'date-fns';

type ZoomMode = 'days' | 'weeks' | 'months';
import { ChevronLeft, ChevronRight, Plus, Edit2, Trash2, X, Check, CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '@/lib/utils';

export interface GanttTask {
  id: string;
  title: string;
  startDate: string; // ISO date string: "YYYY-MM-DD"
  endDate: string;   // ISO date string: "YYYY-MM-DD"
  color?: string;
  category?: string;
  progress?: number; // 0-100
  linkedTableId?: string; // id do componente de tabela ao qual a tarefa pertence
}

interface GanttWidgetProps {
  title?: string;
  tasks?: GanttTask[];
  onTasksChange?: (tasks: GanttTask[]) => void;
  editable?: boolean;
  viewMonths?: number;
  defaultZoomMode?: 'days' | 'weeks' | 'months';
  showProgress?: boolean;
  availableTables?: { id: string; label: string }[];
  filterTableId?: string;
}

const ROW_HEIGHT = 44;         // px per task row
const HEADER_MONTH_H = 30;     // month label row height
const HEADER_DAY_H = 34;       // day number row height
const LABEL_WIDTH = 210;       // task name column width

const TASK_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function safeParseDate(value: string): Date | null {
  if (!value) return null;
  const d = parseISO(value);
  return isValid(d) ? d : null;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export default function GanttWidget({
  title = 'Cronograma',
  tasks: initialTasks = [],
  onTasksChange,
  editable = true,
  viewMonths = 2,
  defaultZoomMode = 'days',
  showProgress = true,
  availableTables = [],
  filterTableId,
}: GanttWidgetProps) {
  const today = startOfDay(new Date());

  // The view starts at the beginning of the current month by default
  const [viewStart, setViewStart] = useState<Date>(() => startOfMonth(today));
  const [tasks, setTasks] = useState<GanttTask[]>(initialTasks);

  const [editingTask, setEditingTask] = useState<GanttTask | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [zoomMode, setZoomMode] = useState<ZoomMode>(defaultZoomMode);

  const timelineRef = useRef<HTMLDivElement>(null);
  const [timelineWidth, setTimelineWidth] = useState(0);

  // Keep internal state in sync when prop changes
  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  // Quando um filtro de Tabela Vinculada está ativo, só exibe as tarefas
  // daquela tabela; sem filtro ativo, mostra todas.
  const visibleTasks = filterTableId
    ? tasks.filter((t) => t.linkedTableId === filterTableId)
    : tasks;

  // Measure timeline width for dynamic day column sizing
  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setTimelineWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Derived date range ─────────────────────────────────────────────────
  const viewEnd =
    zoomMode === 'days'   ? endOfMonth(addMonths(viewStart, viewMonths - 1)) :
    zoomMode === 'weeks'  ? addDays(viewStart, 12 * 7 - 1) :
    /* months */            endOfMonth(addMonths(viewStart, 11));
  const totalDays = differenceInCalendarDays(viewEnd, viewStart) + 1;
  const DAY_WIDTH = timelineWidth > 0 ? timelineWidth / totalDays : 34;

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handlePrev = () => setViewStart((d) =>
    zoomMode === 'days'  ? startOfMonth(subMonths(d, 1)) :
    zoomMode === 'weeks' ? startOfISOWeek(addDays(d, -4 * 7)) :
    startOfMonth(subMonths(d, 12))
  );
  const handleNext = () => setViewStart((d) =>
    zoomMode === 'days'  ? startOfMonth(addMonths(d, 1)) :
    zoomMode === 'weeks' ? startOfISOWeek(addDays(d, 4 * 7)) :
    startOfMonth(addMonths(d, 12))
  );
  const handleToday = () => setViewStart(
    zoomMode === 'weeks' ? startOfISOWeek(today) : startOfMonth(today)
  );


  // ── Task CRUD ──────────────────────────────────────────────────────────────
  const openNew = () => {
    const defaultColor = TASK_COLORS[tasks.length % TASK_COLORS.length];
    setEditingTask({
      id: generateId(),
      title: '',
      startDate: format(today, 'yyyy-MM-dd'),
      endDate: format(addDays(today, 6), 'yyyy-MM-dd'),
      color: defaultColor,
      progress: 0,
      linkedTableId: filterTableId,
    });
    setIsNew(true);
    setIsDialogOpen(true);
  };

  const openEdit = (task: GanttTask) => {
    setEditingTask({ ...task });
    setIsNew(false);
    setIsDialogOpen(true);
  };

  const handleSave = () => {
    if (!editingTask || !editingTask.title.trim()) return;
    const updated = isNew
      ? [...tasks, editingTask]
      : tasks.map((t) => (t.id === editingTask.id ? editingTask : t));
    setTasks(updated);
    onTasksChange?.(updated);
    setIsDialogOpen(false);
    setEditingTask(null);
  };

  const handleDelete = (id: string) => {
    const updated = tasks.filter((t) => t.id !== id);
    setTasks(updated);
    onTasksChange?.(updated);
  };

  // ── Bar geometry ───────────────────────────────────────────────────────────
  const getBarStyle = (task: GanttTask) => {
    const start = safeParseDate(task.startDate);
    const end = safeParseDate(task.endDate);
    if (!start || !end) return null;

    // Clip to visible range
    const clampedStart = isAfter(start, viewEnd) || isBefore(end, viewStart) ? null : start;
    const clampedEnd = clampedStart;
    if (!clampedStart || !clampedEnd) return null;

    const displayStart = isBefore(start, viewStart) ? viewStart : start;
    const displayEnd = isAfter(end, viewEnd) ? viewEnd : end;

    const offsetDays = differenceInCalendarDays(displayStart, viewStart);
    const spanDays = differenceInCalendarDays(displayEnd, displayStart) + 1;

    return {
      left: offsetDays * DAY_WIDTH,
      width: Math.max(spanDays * DAY_WIDTH - 2, DAY_WIDTH - 2),
      color: task.color || '#3b82f6',
      progress: clamp(task.progress ?? 0, 0, 100),
      isPartial: isBefore(start, viewStart) || isAfter(end, viewEnd),
      isOutOfView: isAfter(start, viewEnd) || isBefore(end, viewStart),
    };
  };

  // ── Month-group headers ────────────────────────────────────────────────────
  const monthGroups: { label: string; days: number; start: Date }[] = [];
  let cursor = new Date(viewStart);
  while (isBefore(cursor, viewEnd) || isSameDay(cursor, viewEnd)) {
    const monthEnd = endOfMonth(cursor);
    const groupEnd = isAfter(monthEnd, viewEnd) ? viewEnd : monthEnd;
    const days = differenceInCalendarDays(groupEnd, cursor) + 1;
    monthGroups.push({ label: `${MONTHS_PT[cursor.getMonth()]} ${cursor.getFullYear()}`, days, start: new Date(cursor) });
    cursor = addDays(groupEnd, 1);
  }

  // Year groups (row-1 for months mode)
  const yearGroups: { label: string; days: number }[] = [];
  let yCursor = new Date(viewStart);
  while (isBefore(yCursor, viewEnd) || isSameDay(yCursor, viewEnd)) {
    const yearEnd = new Date(yCursor.getFullYear(), 11, 31);
    const groupEnd = isAfter(yearEnd, viewEnd) ? viewEnd : yearEnd;
    const days = differenceInCalendarDays(groupEnd, yCursor) + 1;
    yearGroups.push({ label: String(yCursor.getFullYear()), days });
    yCursor = addDays(groupEnd, 1);
  }

  // Week cells (row-2 for weeks mode)
  const allWeeks: Date[] = [];
  let wCursor = new Date(viewStart);
  while (!isAfter(wCursor, viewEnd)) {
    allWeeks.push(new Date(wCursor));
    wCursor = addDays(wCursor, 7);
  }

  // ── Day cells ──────────────────────────────────────────────────────────────
  const allDays: Date[] = [];
  for (let i = 0; i < totalDays; i++) {
    allDays.push(addDays(viewStart, i));
  }

  const todayOffset = differenceInCalendarDays(today, viewStart);
  const showTodayLine = todayOffset >= 0 && todayOffset < totalDays;

  // ── Render ─────────────────────────────────────────────────────────────────
  const HEADER_H = HEADER_MONTH_H + HEADER_DAY_H;

  return (
    <>
    <Card className="h-full flex flex-col overflow-hidden">
      {/* ── Toolbar ── */}
      <CardHeader className="flex-none px-4 py-3 border-b bg-card">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-primary" />
            <CardTitle className="text-sm font-semibold tracking-tight">{title}</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <div className="flex items-center rounded-lg border overflow-hidden shadow-sm">
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrev}
                className="rounded-none h-7 w-7 p-0 border-r hover:bg-muted"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToday}
                className="rounded-none h-7 px-3 text-xs font-medium hover:bg-muted border-r"
              >
                Hoje
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNext}
                className="rounded-none h-7 w-7 p-0 hover:bg-muted"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
            {/* Zoom mode switcher */}
            <div className="flex items-center rounded-lg border overflow-hidden shadow-sm">
              {(["days", "weeks", "months"] as ZoomMode[]).map((mode, i, arr) => (
                <Button
                  key={mode}
                  variant="ghost"
                  size="sm"
                  onClick={() => setZoomMode(mode)}
                  className={cn(
                    "rounded-none h-7 px-2.5 text-xs font-medium hover:bg-muted",
                    i < arr.length - 1 && "border-r",
                    zoomMode === mode && "bg-muted text-foreground font-semibold",
                  )}
                >
                  {mode === "days" ? "Dias" : mode === "weeks" ? "Semanas" : "Meses"}
                </Button>
              ))}
            </div>
            {editable && (
              <Button size="sm" onClick={openNew} className="h-7 px-3 text-xs gap-1.5 ml-1">
                <Plus className="w-3.5 h-3.5" />
                Nova tarefa
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden p-0 flex flex-col">
        <div className="flex flex-1 overflow-hidden">

          {/* ── Left: task labels ── */}
          <div
            className="flex-none border-r bg-card z-10 flex flex-col"
            style={{ width: LABEL_WIDTH }}
          >
            {/* Header spacer matching the two header rows */}
            <div
              className="flex-none border-b bg-muted/30 flex items-end pb-1.5 px-3"
              style={{ height: HEADER_H }}
            >
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                Tarefa
              </span>
            </div>

            {/* Task rows */}
            <div className="flex-1 overflow-hidden">
              {visibleTasks.map((task, idx) => (
                <div
                  key={task.id}
                  className={cn(
                    'flex items-center gap-2 px-3 border-b group transition-colors hover:bg-muted/30',
                    idx % 2 === 1 && 'bg-muted/10',
                  )}
                  style={{ height: ROW_HEIGHT }}
                >
                  {/* Color dot */}
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-none ring-1 ring-black/10"
                    style={{ backgroundColor: task.color || '#3b82f6' }}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium truncate block leading-tight" title={task.title}>
                      {task.title}
                    </span>
                    {task.category && (
                      <span className="text-[10px] text-muted-foreground/70 truncate block leading-tight mt-0.5">
                        {task.category}
                      </span>
                    )}
                  </div>
                  {editable && (
                    <div className="flex-none flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openEdit(task)}
                        className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(task.id)}
                        className="p-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {visibleTasks.length === 0 && (
                <div
                  className="flex flex-col items-center justify-center gap-2 text-muted-foreground/50"
                  style={{ height: ROW_HEIGHT * 4 }}
                >
                  <CalendarDays className="w-6 h-6" />
                  <span className="text-xs">
                    {tasks.length === 0 ? 'Nenhuma tarefa' : 'Nenhuma tarefa nesta tabela'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Right: timeline ── */}
          <div className="flex-1 overflow-hidden flex flex-col min-w-0">

            {/* Header */}
            <div className="flex-none select-none border-b overflow-hidden">
              <div style={{ width: totalDays * DAY_WIDTH }}>

                {/* Row 1: month groups (days/weeks mode) or year groups (months mode) */}
                <div className="flex bg-muted/30" style={{ height: HEADER_MONTH_H }}>
                  {zoomMode !== "months" && monthGroups.map((mg, i) => (
                    <div
                      key={i}
                      className="relative border-r flex items-center justify-center overflow-hidden"
                      style={{ width: mg.days * DAY_WIDTH }}
                    >
                      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/80 px-1 truncate">
                        {mg.label}
                      </span>
                    </div>
                  ))}
                  {zoomMode === "months" && yearGroups.map((yg, i) => (
                    <div
                      key={i}
                      className="relative border-r flex items-center justify-center overflow-hidden"
                      style={{ width: yg.days * DAY_WIDTH }}
                    >
                      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/80 px-1 truncate">
                        {yg.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Row 2: day cells (days mode) */}
                {zoomMode === "days" && (
                  <div className="flex" style={{ height: HEADER_DAY_H }}>
                    {allDays.map((day, i) => {
                      const isToday = isSameDay(day, today);
                      const weekend = isWeekend(day);
                      const monday = isMonday(day);
                      return (
                        <div
                          key={i}
                          className={cn(
                            "relative border-r flex flex-col items-center justify-center",
                            weekend ? "bg-muted/50" : "bg-card",
                            isToday && "!bg-primary/15",
                            monday && !weekend && "border-l border-l-border/50",
                          )}
                          style={{ width: DAY_WIDTH, minWidth: DAY_WIDTH }}
                        >
                          <span className={cn("text-[11px] font-medium leading-none", weekend ? "text-muted-foreground/50" : "text-foreground/70", isToday && "!text-primary font-bold")}>
                            {format(day, "d")}
                          </span>
                          {isToday && <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Row 2: week cells (weeks mode) */}
                {zoomMode === "weeks" && (
                  <div className="flex" style={{ height: HEADER_DAY_H }}>
                    {allWeeks.map((weekStart, i) => {
                      const weekEnd = addDays(weekStart, 6);
                      const isCurrentWeek = !isBefore(today, weekStart) && !isAfter(today, weekEnd);
                      return (
                        <div
                          key={i}
                          className={cn(
                            "relative border-r flex flex-col items-center justify-center",
                            isCurrentWeek ? "!bg-primary/15" : i % 2 === 1 ? "bg-muted/30" : "bg-card",
                          )}
                          style={{ width: 7 * DAY_WIDTH }}
                        >
                          <span className={cn("text-[11px] font-medium leading-none", isCurrentWeek && "!text-primary font-bold")}>
                            {format(weekStart, "d/M")}
                          </span>
                          {isCurrentWeek && <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Row 2: month cells (months mode) */}
                {zoomMode === "months" && (
                  <div className="flex" style={{ height: HEADER_DAY_H }}>
                    {monthGroups.map((mg, i) => {
                      const isCurMonth = mg.start.getMonth() === today.getMonth() && mg.start.getFullYear() === today.getFullYear();
                      return (
                        <div
                          key={i}
                          className={cn(
                            "relative border-r flex flex-col items-center justify-center overflow-hidden",
                            isCurMonth ? "!bg-primary/15" : i % 2 === 1 ? "bg-muted/30" : "bg-card",
                          )}
                          style={{ width: mg.days * DAY_WIDTH }}
                        >
                          <span className={cn("text-[11px] font-medium leading-none truncate px-1", isCurMonth && "!text-primary font-bold")}>
                            {MONTHS_PT[mg.start.getMonth()].slice(0, 3)}
                          </span>
                          {isCurMonth && <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />}
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            </div>

            {/* Body */}
            <div ref={timelineRef} className="flex-1 overflow-y-auto overflow-x-hidden">
              <div
                className="relative"
                style={{
                  width: totalDays * DAY_WIDTH,
                  minHeight: `max(${Math.max(visibleTasks.length, 1) * ROW_HEIGHT}px, 100%)`,
                }}
              >
                {/* Column backgrounds */}
                <div className="absolute inset-0 flex pointer-events-none">
                  {zoomMode === "days" && allDays.map((day, i) => (
                    <div
                      key={i}
                      className={cn("border-r h-full", isWeekend(day) ? "bg-muted/30" : "", isSameDay(day, today) ? "bg-primary/8" : "")}
                      style={{ width: DAY_WIDTH }}
                    />
                  ))}
                  {zoomMode === "weeks" && allWeeks.map((ws, i) => {
                    const isCurrentWeek = !isBefore(today, ws) && !isAfter(today, addDays(ws, 6));
                    return <div key={i} className={cn("border-r h-full", i % 2 === 1 ? "bg-muted/20" : "", isCurrentWeek ? "bg-primary/8" : "")} style={{ width: 7 * DAY_WIDTH }} />;
                  })}
                  {zoomMode === "months" && monthGroups.map((mg, i) => {
                    const isCurMonth = mg.start.getMonth() === today.getMonth() && mg.start.getFullYear() === today.getFullYear();
                    return <div key={i} className={cn("border-r h-full", i % 2 === 1 ? "bg-muted/20" : "", isCurMonth ? "bg-primary/8" : "")} style={{ width: mg.days * DAY_WIDTH }} />;
                  })}
                </div>

                {/* Today vertical accent */}
                {showTodayLine && (
                  <div
                    className="absolute top-0 bottom-0 w-px bg-primary/40 z-10 pointer-events-none"
                    style={{ left: todayOffset * DAY_WIDTH + DAY_WIDTH / 2 }}
                  />
                )}

                {/* Task rows */}
                {visibleTasks.map((task, idx) => {
                  const bar = getBarStyle(task);
                  return (
                    <div
                      key={task.id}
                      className={cn(
                        'relative border-b flex items-center transition-colors hover:bg-primary/5',
                        idx % 2 === 1 && 'bg-muted/5',
                      )}
                      style={{ height: ROW_HEIGHT }}
                    >
                      {bar && !bar.isOutOfView && (
                        <div
                          className="absolute top-1/2 -translate-y-1/2 rounded-full flex items-center overflow-hidden cursor-default select-none"
                          style={{
                            left: bar.left + 2,
                            width: bar.width - 2,
                            height: ROW_HEIGHT - 18,
                            backgroundColor: bar.color,
                            boxShadow: `0 1px 3px ${bar.color}55, inset 0 1px 0 rgba(255,255,255,0.15)`,
                          }}
                          title={`${task.title} | ${task.startDate} → ${task.endDate}${task.progress ? ` | ${task.progress}%` : ''}`}
                        >
                          {/* Progress overlay – lighter fill at start */}
                          {showProgress && bar.progress > 0 && (
                            <div
                              className="absolute inset-y-0 left-0 bg-white/25 rounded-full"
                              style={{ width: `${bar.progress}%` }}
                            />
                          )}
                          {/* Label */}
                          <span className="relative z-10 text-white text-[11px] px-2 truncate leading-none font-semibold drop-shadow-sm">
                            {task.title}
                            {showProgress && bar.progress > 0 && (
                              <span className="ml-1.5 opacity-75 text-[10px] font-normal">{bar.progress}%</span>
                            )}
                          </span>
                          {/* Left partial indicator */}
                          {bar.isPartial && isBefore(safeParseDate(task.startDate)!, viewStart) && (
                            <div className="absolute left-0 top-0 bottom-0 w-2 bg-black/15 rounded-l-full" />
                          )}
                          {/* Right partial indicator */}
                          {bar.isPartial && isAfter(safeParseDate(task.endDate)!, viewEnd) && (
                            <div className="absolute right-0 top-0 bottom-0 w-2 bg-black/15 rounded-r-full" />
                          )}
                        </div>
                      )}

                      {/* Out-of-view arrow hints */}
                      {bar?.isOutOfView && (() => {
                        const start = safeParseDate(task.startDate)!;
                        const goLeft = isAfter(viewStart, start);
                        return (
                          <div
                            className={cn(
                              'absolute top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] text-muted-foreground/50 px-2',
                              goLeft ? 'left-0' : 'right-0',
                            )}
                          >
                            {goLeft ? '‹' : '›'}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}

                {/* Empty state */}
                {visibleTasks.length === 0 && (
                  <div
                    className="flex flex-col items-center justify-center gap-2 text-muted-foreground/40"
                    style={{ height: ROW_HEIGHT * 5 }}
                  >
                    <span className="text-xs">
                      {tasks.length === 0 ? 'Adicione tarefas para começar' : 'Nenhuma tarefa nesta tabela'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

      {/* ── Edit / Create dialog ── */}
      <Dialog open={isDialogOpen} onOpenChange={(o) => { if (!o) setIsDialogOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isNew ? (
                <><Plus className="w-4 h-4 text-primary" /> Nova Tarefa</>
              ) : (
                <><Edit2 className="w-4 h-4 text-primary" /> Editar Tarefa</>
              )}
            </DialogTitle>
          </DialogHeader>

          {editingTask && (
            <div className="grid gap-4 py-2">
              <div className="grid gap-1.5">
                <Label>Título</Label>
                <Input
                  value={editingTask.title}
                  onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                  placeholder="Nome da tarefa"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>Início</Label>
                  <Input
                    type="date"
                    value={editingTask.startDate}
                    onChange={(e) => setEditingTask({ ...editingTask, startDate: e.target.value })}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label>Fim</Label>
                  <Input
                    type="date"
                    value={editingTask.endDate}
                    min={editingTask.startDate}
                    onChange={(e) => setEditingTask({ ...editingTask, endDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label className="flex items-center justify-between">
                  Progresso
                  <Badge variant="secondary" className="text-xs font-mono">{editingTask.progress ?? 0}%</Badge>
                </Label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={editingTask.progress ?? 0}
                  onChange={(e) => setEditingTask({ ...editingTask, progress: Number(e.target.value) })}
                  className="w-full accent-primary"
                />
              </div>

              <div className="grid gap-1.5">
                <Label>Cor</Label>
                <div className="flex gap-2 flex-wrap items-center">
                  {TASK_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setEditingTask({ ...editingTask, color: c })}
                      className={cn(
                        'w-6 h-6 rounded-full border-2 transition-all hover:scale-110',
                        editingTask.color === c
                          ? 'border-foreground scale-110 ring-2 ring-offset-2 ring-foreground/20'
                          : 'border-transparent',
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                  <input
                    type="color"
                    value={editingTask.color || '#3b82f6'}
                    onChange={(e) => setEditingTask({ ...editingTask, color: e.target.value })}
                    className="w-6 h-6 rounded cursor-pointer border border-input bg-transparent"
                    title="Cor personalizada"
                  />
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label>
                  Categoria{' '}
                  <span className="text-muted-foreground text-xs font-normal">(opcional)</span>
                </Label>
                <Input
                  value={editingTask.category || ''}
                  onChange={(e) => setEditingTask({ ...editingTask, category: e.target.value })}
                  placeholder="ex: Marketing, Dev, Design…"
                />
              </div>

              {availableTables.length > 0 && (
                <div className="grid gap-1.5">
                  <Label>
                    Tabela{' '}
                    <span className="text-muted-foreground text-xs font-normal">(opcional)</span>
                  </Label>
                  <Select
                    value={editingTask.linkedTableId || '__none__'}
                    onValueChange={(value) =>
                      setEditingTask({
                        ...editingTask,
                        linkedTableId: value === '__none__' ? undefined : value,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Nenhuma tabela vinculada" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nenhuma</SelectItem>
                      {availableTables.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setIsDialogOpen(false)}>
              <X className="w-3.5 h-3.5 mr-1.5" /> Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!editingTask?.title?.trim()}>
              <Check className="w-3.5 h-3.5 mr-1.5" /> Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
